/**
 * Custom escape function to replace deprecated escape()
 * This function performs the same operation as the deprecated escape()
 * which was used to encode Unicode characters for URL encoding
 */
export function myEscape(str: string): string {
  return str.replace(/[\u0080-\uFFFF]/g, (match) => {
    const code = match.charCodeAt(0)
    if (code < 256) {
      return `%${code.toString(16).padStart(2, '0').toUpperCase()}`
    } else {
      return `%u${code.toString(16).padStart(4, '0').toUpperCase()}`
    }
  })
}

/**
 * Custom unescape function to replace deprecated unescape()
 * This function performs the same operation as the deprecated unescape()
 * which was used to decode Unicode characters from URL encoding
 */
export function myUnescape(str: string): string {
  return str.replace(/%u([0-9a-fA-F]{4})/g, (match, digits) => {
    return String.fromCharCode(Number.parseInt(digits, 16))
  }).replace(/%([0-9a-f]{2})/gi, (match, digits) => {
    return String.fromCharCode(Number.parseInt(digits, 16))
  })
}

/**
 * Encodes a string to base64 with proper UTF-8 handling
 */
export function encodeBase64(value: string): string {
  return btoa(myUnescape(encodeURIComponent(value)))
}

/**
 * Decodes a base64 string with proper UTF-8 handling
 */
export function decodeBase64(value: string): string {
  return decodeURIComponent(myEscape(atob(value)))
}

/**
 * Converts base64 string to JSON object
 */
export function base64ToJson(value: string): any {
  return JSON.parse(decodeBase64(value))
}

/**
 * Converts object to base64 string
 */
export function jsonToBase64(value: any): string {
  return encodeBase64(JSON.stringify(value))
}

/**
 * Simple base64 encoding using btoa()
 */
export function Encode64(obj: string): string {
  return obj ? btoa(obj) : ''
}

/**
 * Simple base64 decoding using atob()
 * Retorna '' quando a entrada é vazia ou não é base64 válido, em vez de
 * lançar InvalidCharacterError e quebrar a aplicação (ex.: Revisao.vue).
 */
export function Decode64(obj: string): string {
  if (!obj)
    return ''
  try {
    return atob(obj)
  } catch {
    return ''
  }
}

/**
 * Codifica uma string em base64 com UTF-8 de forma robusta (TextEncoder).
 * Diferente de `Encode64`/`Encode64UTF8`, é seguro para qualquer caractere
 * (inclusive `%` literal) e não depende de encode/decodeURIComponent nem de
 * `btoa` direto (que quebra com code points > 255).
 */
export function toBase64Utf8(str: string): string {
  if (!str)
    return ''
  const bytes = new TextEncoder().encode(str)
  let binary = ''
  for (const byte of bytes)
    binary += String.fromCharCode(byte)
  return btoa(binary)
}

/**
 * Decodifica uma string base64 (UTF-8) de forma robusta (TextDecoder).
 * Par inverso de `toBase64Utf8`.
 */
export function fromBase64Utf8(b64: string): string {
  if (!b64)
    return ''
  const binary = atob(b64)
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
  return new TextDecoder('utf-8').decode(bytes)
}

/**
 * Validates if a string is valid base64
 */
export function isValidBase64(str: string): boolean {
  try {
    atob(str)
    return true
  } catch {
    return false
  }
}

/**
 * Decodes base64 and parses as JSON
 */
export function DecodeData(obj: string): any {
  return obj ? JSON.parse(Decode64(obj)) : []
}

/**
 * Stringifies and encodes to base64
 */
export function EncodeData(obj: any): string {
  return obj ? Encode64(JSON.stringify(obj)) : ''
}
